import { CreateCommentInput, DeleteCommentInput, GetEventCommentsResult } from "@/apiTypes/comment";
import { authMiddleware, optionalAuthMiddleware } from "@/helpers/authMiddleware";
import { prismaClient } from "@/helpers/prismaClient.server";
import {
  extractRequestContext,
  trackEvent,
} from "@/helpers/mixpanel.server";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

/**
 * Fetches comments for an event with visibility gating:
 * - Unauthenticated: returns totalCount only (no comment content) as a teaser
 * - Authenticated, not registered and not organizer admin: COMMENT type only
 * - Registered (APPROVED or PENDING_PREREQUISITE_REVIEW) or organizer admin: COMMENT + UPDATE types
 *
 * Soft-deleted comments are included so the component can render placeholders.
 * Only top-level comments (parentId = null) are returned in this slice.
 */
export const getEventComments = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error("Invalid event ID format");
    return result.data;
  })
  .handler(async ({ data: eventId, context }) => {
    const auth = context.auth;
    const db = await prismaClient();

    if (!auth) {
      const totalCount = await db.comment.count({
        where: { eventId, deletedAt: null, type: "COMMENT", parentId: null },
      });
      return GetEventCommentsResult.parse({
        comments: [],
        totalCount,
        organizerName: null,
      });
    }

    const [registration, event] = await Promise.all([
      db.registration.findFirst({
        where: {
          profileId: auth.profile.id,
          eventId,
          status: { in: ['APPROVED', 'PENDING_PREREQUISITE_REVIEW', 'PAID', 'HOLD'] },
        },
        select: { status: true },
        orderBy: { createdAt: 'desc' },
      }),
      db.event.findUnique({
        where: { id: eventId },
        select: { organizerId: true },
      }),
    ]);

    const isRegistered =
      registration?.status === "APPROVED" ||
      registration?.status === "PENDING_PREREQUISITE_REVIEW" ||
      registration?.status === "PAID";

    // Organizer admins can see UPDATE comments regardless of registration status.
    const currentUserAdminRow = event?.organizerId
      ? await db.organizerAdmin.findUnique({
          where: {
            profileId_organizerId: {
              profileId: auth.profile.id,
              organizerId: event.organizerId,
            },
          },
        })
      : null;
    const isOrganizerAdmin = currentUserAdminRow !== null;

    const canSeeUpdates = isRegistered || isOrganizerAdmin;

    const [rawComments, totalCount, organizerAdmins, organizer] =
      await Promise.all([
        db.comment.findMany({
          where: {
            eventId,
            deletedAt: null,
            type: canSeeUpdates ? { in: ["COMMENT", "UPDATE"] } : "COMMENT",
            parentId: null,
          },
          orderBy: { createdAt: "desc" },
          select: {
            id: true,
            createdAt: true,
            updatedAt: true,
            content: true,
            type: true,
            deletedAt: true,
            eventId: true,
            authorProfileId: true,
            parentId: true,
            authorProfile: {
              select: {
                id: true,
                displayName: true,
                firstName: true,
                lastName: true,
                avatarUrl: true,
              },
            },
          },
        }),
        db.comment.count({
          where: {
            eventId,
            type: canSeeUpdates ? { in: ["COMMENT", "UPDATE"] } : "COMMENT",
            parentId: null,
            deletedAt: null,
          },
        }),
        event?.organizerId
          ? db.organizerAdmin.findMany({
              where: { organizerId: event.organizerId },
              select: { profileId: true },
            })
          : Promise.resolve([]),
        event?.organizerId
          ? db.organizer.findUnique({
              where: { id: event.organizerId },
              select: { name: true },
            })
          : Promise.resolve(null),
      ]);

    const organizerAdminProfileIds = new Set(
      organizerAdmins.map((a) => a.profileId),
    );

    const enrichedComments = rawComments.map((c) => ({
      ...c,
      isOrganizerAdmin: organizerAdminProfileIds.has(c.authorProfileId),
    }));

    // Sort UPDATE comments newest-first before COMMENT comments newest-first.
    // Prisma orderBy can't sort by enum priority, so we sort in JS.
    const comments = [
      ...enrichedComments.filter((c) => c.type === "UPDATE"),
      ...enrichedComments.filter((c) => c.type === "COMMENT"),
    ];

    return GetEventCommentsResult.parse({
      comments,
      totalCount,
      organizerName: organizer?.name ?? null,
    });
  });

/** The comment select shape reused across create/fetch to ensure consistent return types. */
const commentSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  content: true,
  type: true,
  deletedAt: true,
  eventId: true,
  authorProfileId: true,
  parentId: true,
  authorProfile: {
    select: {
      id: true,
      displayName: true,
      firstName: true,
      lastName: true,
      avatarUrl: true,
    },
  },
} as const;

/** Helper to add isOrganizerAdmin flag to a comment result. */
async function enrichCommentWithOrganizerFlag(
  comment: {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    content: string;
    type: "COMMENT" | "UPDATE";
    deletedAt: Date | null;
    eventId: string;
    authorProfileId: string;
    parentId: string | null;
    authorProfile: {
      id: string;
      displayName: string | null;
      firstName: string | null;
      lastName: string | null;
      avatarUrl: string | null;
    };
  },
  eventId: string,
  db: Awaited<ReturnType<typeof prismaClient>>,
) {
  const event = await db.event.findUnique({
    where: { id: eventId },
    select: { organizerId: true },
  });

  let isOrganizerAdmin = false;
  if (event?.organizerId) {
    const membership = await db.organizerAdmin.findUnique({
      where: {
        profileId_organizerId: {
          profileId: comment.authorProfileId,
          organizerId: event.organizerId,
        },
      },
    });
    isOrganizerAdmin = membership !== null;
  }

  return { ...comment, isOrganizerAdmin };
}

/**
 * Creates a new comment on an event.
 * Requires authentication. Non-admins are limited to 1000 characters.
 */
export const createComment = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .inputValidator((data: unknown) => {
    const result = CreateCommentInput.safeParse(data);
    if (!result.success) throw new Error("Invalid input");
    return result.data;
  })
  .handler(async ({ data: { eventId, content, type }, context }) => {
    const auth = context.auth;
    const db = await prismaClient();

    const event = await db.event.findUnique({
      where: { id: eventId },
      select: { organizerId: true },
    });

    if (!event) throw new Error("Event not found");

    let isOrganizerAdmin = false;
    if (event.organizerId) {
      const membership = await db.organizerAdmin.findUnique({
        where: {
          profileId_organizerId: {
            profileId: auth.profile.id,
            organizerId: event.organizerId,
          },
        },
      });
      isOrganizerAdmin = membership !== null;
    }

    if (type === "UPDATE" && !isOrganizerAdmin) {
      throw new Error("Only organizer admins can post updates");
    }

    if (!isOrganizerAdmin && content.length > 1000) {
      throw new Error("Comment exceeds the 1000-character limit");
    }

    const comment = await db.comment.create({
      data: { eventId, authorProfileId: auth.profile.id, content, type },
      select: commentSelect,
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      auth.profile.id,
      'Comment Created',
      {
        event_id: eventId,
        comment_type: type,
      },
      requestContext,
    );

    return enrichCommentWithOrganizerFlag(comment, eventId, db);
  });

/**
 * Soft-deletes a comment by setting its deletedAt timestamp.
 * Requires authentication. The user must be the comment author
 * or an organizer admin of the event the comment belongs to.
 */
export const deleteComment = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = DeleteCommentInput.safeParse(data);
    if (!result.success) throw new Error("Invalid comment ID format");
    return result.data;
  })
  .handler(async ({ data: commentId, context }) => {
    const auth = context.auth;
    const db = await prismaClient();

    const comment = await db.comment.findUnique({
      where: { id: commentId },
      select: { authorProfileId: true, eventId: true },
    });

    if (!comment) throw new Error("Comment not found");

    const isAuthor = comment.authorProfileId === auth.profile.id;

    let isOrganizerAdmin = false;
    if (!isAuthor) {
      const event = await db.event.findUnique({
        where: { id: comment.eventId },
        select: { organizerId: true },
      });

      if (event?.organizerId) {
        const membership = await db.organizerAdmin.findUnique({
          where: {
            profileId_organizerId: {
              profileId: auth.profile.id,
              organizerId: event.organizerId,
            },
          },
        });
        isOrganizerAdmin = membership !== null;
      }
    }

    if (!isAuthor && !isOrganizerAdmin) {
      throw new Error("You do not have permission to delete this comment");
    }

    await db.comment.update({
      where: { id: commentId },
      data: { deletedAt: new Date() },
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      auth.profile.id,
      "Comment Deleted",
      {
        event_id: comment.eventId,
        comment_id: commentId,
        is_own_comment: isAuthor,
      },
      requestContext,
    );

    return { success: true };
  });
